import { getAccessToken, getScopeHeaders } from '../lib/auth-storage';

const API_BASE = import.meta.env.VITE_PRICING_API ?? '/api/v1';

export type Currency = 'INR' | 'USD' | 'EUR' | 'GBP';

export type PriceBook = {
  id: string;
  tenant_id: string;
  organization_id: string;
  code: string;
  name: string;
  description?: string;
  currency: Currency;
  status: string;
  version: number;
  created_at: string;
  updated_at: string;
};

export type ProductPrice = {
  id: string;
  price_book_id: string;
  product_id: string;
  sku?: string;
  base_price: number;
  cost_price?: number;
  currency: Currency;
  status: string;
  min_price?: number;
  max_price?: number;
  created_at: string;
  updated_at: string;
};

export type MarketplacePrice = {
  id: string;
  product_id: string;
  marketplace: string;
  list_price: number;
  sale_price?: number;
  currency: Currency;
  created_at: string;
  updated_at: string;
};

export type Promotion = {
  id: string;
  code: string;
  name: string;
  promotion_type: string;
  status: string;
  starts_at?: string;
  ends_at?: string;
  created_at: string;
  updated_at: string;
};

export type Coupon = {
  id: string;
  code: string;
  discount_type: string;
  value: number;
  status: string;
  max_uses?: number;
  used_count?: number;
  created_at: string;
  updated_at: string;
};

export type PricingRecommendation = {
  product_id: string;
  current_price: number;
  recommended: number;
  min_price?: number;
  max_price?: number;
  margin_percent?: number;
  confidence: number;
  reason?: string;
  source?: string;
};

export type PricingAnalytics = {
  stats?: Record<string, unknown>;
  gross_margin?: number;
  net_margin?: number;
  promotion_effectiveness?: number;
};

export type PricingDashboard = {
  stats?: Record<string, unknown>;
  widgets?: Array<{ id: string; title: string; type: string }>;
};

export type CouponValidation = {
  valid: boolean;
  discount_amount?: number;
  message?: string;
};

function headers(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...getScopeHeaders(),
    ...(getAccessToken() ? { Authorization: `Bearer ${getAccessToken()}` } : {}),
  };
}

async function pricingFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: { ...headers(), ...(init?.headers as Record<string, string> | undefined) },
  });
  if (!res.ok) throw new Error(`Pricing ${path} failed: ${res.status} ${await res.text()}`);
  const body = await res.json();
  return (body.data ?? body) as T;
}

export const pricingApi = {
  dashboard: () => pricingFetch<PricingDashboard>('/pricing/dashboard'),

  listPriceBooks: () => pricingFetch<PriceBook[]>('/price-books'),
  createPriceBook: (input: { code: string; name: string; currency?: Currency; description?: string }) =>
    pricingFetch<PriceBook>('/price-books', { method: 'POST', body: JSON.stringify(input) }),
  updatePriceBook: (id: string, input: Partial<PriceBook>) =>
    pricingFetch<PriceBook>(`/price-books/${id}`, { method: 'PUT', body: JSON.stringify(input) }),
  deletePriceBook: (id: string) => pricingFetch<void>(`/price-books/${id}`, { method: 'DELETE' }),

  listPrices: (params?: { price_book_id?: string; product_id?: string }) => {
    const qs = new URLSearchParams();
    if (params?.price_book_id) qs.set('price_book_id', params.price_book_id);
    if (params?.product_id) qs.set('product_id', params.product_id);
    const q = qs.toString();
    return pricingFetch<ProductPrice[]>(`/prices${q ? `?${q}` : ''}`);
  },
  createPrice: (input: { price_book_id: string; product_id: string; base_price: number; cost_price?: number; currency?: Currency }) =>
    pricingFetch<ProductPrice>('/prices', { method: 'POST', body: JSON.stringify(input) }),
  updatePrice: (id: string, input: Partial<ProductPrice>) =>
    pricingFetch<ProductPrice>(`/prices/${id}`, { method: 'PUT', body: JSON.stringify(input) }),
  bulkUpdatePrices: (input: { price_book_id: string; items: Array<{ product_id: string; base_price: number }>; reason?: string }) =>
    pricingFetch<{ updated: number }>('/prices/bulk-update', { method: 'POST', body: JSON.stringify(input) }),

  listMarketplacePrices: (params?: { product_id?: string; marketplace?: string }) => {
    const qs = new URLSearchParams();
    if (params?.product_id) qs.set('product_id', params.product_id);
    if (params?.marketplace) qs.set('marketplace', params.marketplace);
    const q = qs.toString();
    return pricingFetch<MarketplacePrice[]>(`/marketplace-prices${q ? `?${q}` : ''}`);
  },
  upsertMarketplacePrice: (input: { product_id: string; marketplace: string; list_price: number; sale_price?: number; currency?: Currency }) =>
    pricingFetch<MarketplacePrice>('/marketplace-prices', { method: 'PUT', body: JSON.stringify(input) }),

  listPromotions: () => pricingFetch<Promotion[]>('/promotions'),
  createPromotion: (input: { code: string; name: string; promotion_type: string; status?: string }) =>
    pricingFetch<Promotion>('/promotions', { method: 'POST', body: JSON.stringify(input) }),
  updatePromotion: (id: string, input: Partial<Promotion>) =>
    pricingFetch<Promotion>(`/promotions/${id}`, { method: 'PUT', body: JSON.stringify(input) }),

  listCoupons: () => pricingFetch<Coupon[]>('/coupons'),
  createCoupon: (input: { code: string; discount_type: string; value: number }) =>
    pricingFetch<Coupon>('/coupons', { method: 'POST', body: JSON.stringify(input) }),
  validateCoupon: (input: { code: string; order_amount: number; product_ids?: string[] }) =>
    pricingFetch<CouponValidation>('/coupons/validate', { method: 'POST', body: JSON.stringify(input) }),

  recommend: (input: { product_id: string; price_book_id?: string; product_name?: string }) =>
    pricingFetch<PricingRecommendation>('/pricing/recommend', { method: 'POST', body: JSON.stringify(input) }),
  analytics: () => pricingFetch<PricingAnalytics>('/pricing/analytics'),
};
